import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { NgxPermissionsService } from 'ngx-permissions';
import { take } from 'rxjs';

import { Payment } from '../../../../models/payment.model';
import { User } from '../../../../models/user.model';
import { WidgetSummaryDisplayComponent } from '../../../../shared/components/widget-displays/widget-summary-display/widget-summary-display.component';
import { Permission } from '../../../../shared/constants/permissions';
import { SharedModule } from '../../../../shared/shared.module';

import * as fromStore from '../../../../store';

@Component({
  selector: 'app-ons-widget-summary',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [WidgetSummaryDisplayComponent, SharedModule],
  template: `
    @if (canReadBilling()) {
      <app-widget-summary-display
        [loading]="isLoading()"
        [accountName]="user()?.Account?.Name"
        [billingAddress]="userBillingAddress()"
        [lastInvoiceDate]="invoiceDate()"
        [lastPaymentDate]="payment()?.Transaction_Date__c"
        [lastPaymentAmount]="payment()?.Transaction_Amount__c"
      ></app-widget-summary-display>
    }
  `,
})
export class WidgetSummaryComponent implements OnInit {
  private readonly store = inject(Store<fromStore.State>);

  private readonly permissionsService = inject(NgxPermissionsService);

  readonly canReadBilling = signal(false);

  readonly isLoading = toSignal<boolean>(
    this.store.select(fromStore.getCurrentUserLoading),
  );

  readonly isLoaded = toSignal<boolean>(
    this.store.select(fromStore.getCurrentUserLoaded),
  );

  readonly user = toSignal<User>(this.store.select(fromStore.getCurrentUser));

  readonly userBillingAddress = computed<string>(() => {
    const user = this.user();
    if (!user?.Account?.BillingAddress) return '';

    const { street, city, stateCode, postalCode } = user.Account
      .BillingAddress as Record<string, string>;
    const line1 = street ?? '';
    const cityState = [city, stateCode].filter(Boolean).join(', ');
    const line2 = [cityState, postalCode].filter(Boolean).join(' ');
    return [line1, line2].filter(Boolean).join('\r\n');
  });

  readonly isLoadingPayments = toSignal<boolean>(
    this.store.select(fromStore.getPaymentsLoading),
  );

  readonly invoiceDate = toSignal<Date | null>(
    this.store.select(fromStore.getLatestInvoiceDate),
  );

  readonly payment = toSignal<Payment | null>(
    this.store.select(fromStore.getLatestPayment),
  );

  async ngOnInit(): Promise<void> {
    const canRead = await this.permissionsService.hasPermission(
      Permission.BILLING_READ,
    );
    this.canReadBilling.set(canRead);

    if (canRead) {
      this.initializeBillingData();
    }
  }

  private initializeBillingData(): void {
    this.store
      .select(fromStore.getPaymentsLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadPayments());
        }
      });

    this.store
      .select(fromStore.getInvoicesLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadInvoices());
        }
      });
  }
}
